/**
 * useWizardState — URL ?step= sync + sessionStorage persistence
 * canonical: S1 lines 722–754
 *
 * - Step stored in URL search param `step` (1–5)
 * - Full wizard form state persisted to sessionStorage key `v4_wizard_in_progress`
 *   on every change (debounced 300ms), cleared on wizard completion
 * - No sub-step URL param needed for PR-V4-NEW-PROJECT-WIZARD (sub-steps 2–6 deferred)
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { WizardInProgress } from '../../../api/types'
import { WIZARD_SCHEMA_VERSION } from '../../../api/types'

const STORAGE_KEY = 'v4_wizard_in_progress'
const DEBOUNCE_MS = 300

function loadStorage(): WizardInProgress | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as WizardInProgress
    if ((parsed.schemaVersion ?? 1) < WIZARD_SCHEMA_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

function saveStorage(state: WizardInProgress): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // quota exceeded — non-fatal
  }
}

export function clearWizardStorage(): void {
  sessionStorage.removeItem(STORAGE_KEY)
}

function emptyWizard(): WizardInProgress {
  return {
    schemaVersion: WIZARD_SCHEMA_VERSION,
    projectId: undefined,
    payAppId: undefined,
    step: 1,
    step1: {},
    step1Dirty: {},
    step2Lines: [],
    step5: {},
  }
}

export function useWizardState() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // Derive current step from URL — default to 1
  const urlStep = parseInt(searchParams.get('step') ?? '1', 10)
  const currentStep = urlStep >= 1 && urlStep <= 5 ? urlStep : 1

  // Wizard form state — initialise from sessionStorage
  const [wizard, setWizardRaw] = useState<WizardInProgress>(() => {
    const stored = loadStorage()
    return stored ?? emptyWizard()
  })

  // Debounce ref for sessionStorage writes
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync wizard state to sessionStorage (debounced)
  const setWizard = useCallback((updater: WizardInProgress | ((prev: WizardInProgress) => WizardInProgress)) => {
    setWizardRaw(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => saveStorage(next), DEBOUNCE_MS)
      return next
    })
  }, [])

  // Flush any pending debounced sessionStorage write synchronously.
  const flushDraftSave = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setWizardRaw(current => { saveStorage(current); return current })
  }, [])

  // Navigate to a step — updates URL + wizard.step (flush draft first)
  const goToStep = useCallback((step: number) => {
    flushDraftSave()
    const s = Math.max(1, Math.min(5, step))
    setSearchParams({ step: String(s) }, { replace: true })
    setWizard(prev => ({ ...prev, step: s }))
  }, [setSearchParams, setWizard, flushDraftSave])

  // Sync URL → wizard.step when URL changes externally
  useEffect(() => {
    setWizard(prev => (prev.step !== currentStep ? { ...prev, step: currentStep } : prev))
  }, [currentStep]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [])

  const cancelWizard = useCallback(() => {
    clearWizardStorage()
    navigate('/projects')
  }, [navigate])

  const saveAndExit = useCallback(() => {
    flushDraftSave()
    navigate('/projects')
  }, [navigate, flushDraftSave])

  const completeWizard = useCallback((projectId: number) => {
    clearWizardStorage()
    navigate(`/projects?highlight=${projectId}`)
  }, [navigate])

  return {
    currentStep,
    wizard,
    setWizard,
    goToStep,
    cancelWizard,
    saveAndExit,
    flushDraftSave,
    completeWizard,
  }
}
